import { useEffect } from "react";
import { trackIframeVisibleArea } from "./iframeVisibleArea";

const AUTO_HEIGHT_CLASS = "auto-height";

export const useAutoResizeIframe = () => {
  useEffect(() => {
    const rootElement = document.getElementById("root");
    let lastSentHeight = 0;
    let stopTrackingVisibleArea: (() => void) | undefined;

    const sendHeight = () => {
      const newHeight = rootElement ? rootElement.scrollHeight : document.body.scrollHeight;
      // Only send if height is meaningful and actually changed
      if (newHeight > 0 && newHeight !== lastSentHeight) {
        lastSentHeight = newHeight;
        window.parent.postMessage({ type: "content_height", height: newHeight }, "*");
      }
    };

    const debouncedSendHeight = debounce(sendHeight, 100);

    const detectParentFollowsHeight = () => {
      if (stopTrackingVisibleArea) return;
      if (lastSentHeight > 0 && Math.abs(window.innerHeight - lastSentHeight) <= 1) {
        document.documentElement.classList.add(AUTO_HEIGHT_CLASS);
        stopTrackingVisibleArea = trackIframeVisibleArea();
      }
    };

    const observer = new ResizeObserver(debouncedSendHeight);
    observer.observe(rootElement ?? document.body);
    window.addEventListener("resize", detectParentFollowsHeight);

    // Delay initial height send to let content settle
    const initialTimer = setTimeout(sendHeight, 50);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", detectParentFollowsHeight);
      stopTrackingVisibleArea?.();
      clearTimeout(initialTimer);
    };
  }, []);
};

function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  } as T;
}
